import { useEffect, useRef, useState, type DragEvent } from 'react';
import { clipboardFiles } from '@/components/tasks/clipboard-files';

function carriesFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

/** The part of a FileSystemEntry read here (webkitGetAsEntry), so folders can be walked. */
type FsEntry = {
  isFile: boolean;
  isDirectory: boolean;
  file?: (ok: (file: File) => void, fail: (error: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (entries: FsEntry[]) => void, fail: (error: unknown) => void) => void };
};

/** Every file under a dropped entry. Directory readers return results in pages, so read until empty. */
async function filesUnder(entry: FsEntry): Promise<File[]> {
  if (entry.isFile && entry.file) {
    return new Promise((resolve) => entry.file!((file) => resolve([file]), () => resolve([])));
  }
  if (!entry.isDirectory || !entry.createReader) return [];
  const reader = entry.createReader();
  const children: FsEntry[] = [];
  for (;;) {
    const page = await new Promise<FsEntry[]>((resolve) => reader.readEntries(resolve, () => resolve([])));
    if (page.length === 0) break;
    children.push(...page);
  }
  return (await Promise.all(children.map(filesUnder))).flat();
}

/**
 * Whether a file matches an `accept` list like a file input's: MIME types ("image/png"), wildcards
 * ("image/*") or extensions (".csv").
 */
export function fileMatches(file: File, accept: readonly string[] | undefined): boolean {
  if (!accept || accept.length === 0) return true;
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return accept.some((raw) => {
    const rule = raw.trim().toLowerCase();
    if (rule.startsWith('.')) return name.endsWith(rule);
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

export type FileDropOptions = {
  /** Only files matching these (MIME types, "image/*" or ".ext") are passed on; others are left out. */
  accept?: readonly string[];
  /** Read the files inside dropped folders too (a whole folder of screenshots). */
  folders?: boolean;
  /** Nothing is taken and nothing highlights (busy, read-only). */
  disabled?: boolean;
};

/**
 * Makes any element a drop target for files dragged in from the computer: spread `bind` on it and
 * use `dragging` (files are over it) for a highlight. Drops already handled inside (by a nested
 * target or the chat box) are left alone, and a handled drop doesn't reach targets around it.
 * A dashed "drop files here" panel with a Choose button is FileDropZone, built on this.
 */
export function useFileDrop(onFiles: ((files: File[]) => void) | undefined, options: FileDropOptions = {}) {
  const { accept, folders = false, disabled = false } = options;
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  const deliver = (files: File[]) => {
    const wanted = files.filter((file) => fileMatches(file, accept));
    if (wanted.length) onFiles?.(wanted);
  };

  const bind =
    onFiles && !disabled
      ? {
          onDragEnter: (e: DragEvent) => {
            if (!carriesFiles(e)) return;
            e.preventDefault();
            depth.current += 1;
            setDragging(true);
          },
          onDragOver: (e: DragEvent) => {
            if (!carriesFiles(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          },
          onDragLeave: (e: DragEvent) => {
            if (!carriesFiles(e)) return;
            depth.current = Math.max(0, depth.current - 1);
            if (depth.current === 0) setDragging(false);
          },
          onDrop: (e: DragEvent) => {
            depth.current = 0;
            setDragging(false);
            if (e.defaultPrevented || !carriesFiles(e)) return;
            e.preventDefault();
            e.stopPropagation();
            if (folders) {
              // Entries must be collected before the first await: the browser clears the drop data afterwards.
              const entries = Array.from(e.dataTransfer.items ?? [])
                .map((item) => (item.webkitGetAsEntry?.() ?? null) as FsEntry | null)
                .filter((entry): entry is FsEntry => entry != null);
              if (entries.length > 0) {
                void Promise.all(entries.map(filesUnder)).then((lists) => deliver(lists.flat()));
                return;
              }
            }
            deliver(clipboardFiles(e.dataTransfer));
          },
        }
      : onFiles
        ? {
            // Busy or read-only: still claim file drags, so a drop here doesn't open the file in place of the factory.
            onDragOver: (e: DragEvent) => {
              if (!carriesFiles(e)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'none';
            },
            onDrop: (e: DragEvent) => {
              if (!carriesFiles(e)) return;
              e.preventDefault();
              e.stopPropagation();
            },
          }
        : {};

  return { dragging: dragging && !disabled && onFiles != null, bind };
}

/**
 * While mounted, a file dropped just outside a drop zone doesn't make the browser open it in
 * place of the factory.
 */
export function usePreventFileNavigation(): void {
  useEffect(() => {
    const stop = (e: globalThis.DragEvent) => {
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, []);
}
